import { useQuery } from '@tanstack/react-query';

import { dashMetaFetch } from '../../../../shared/api/dash-meta-fetch';
import { TanstackQueryError } from '../../../../shared/data-fetch/tanstack-query-provider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import { useRoleLeafIds } from '../../filters/hooks/useRoleLeafIds';
import type { PrimaryFilterId } from '../../filters/types/filters';
import {
  generateSentimentPayload,
  MissingRoleLeafIdsError,
} from '../../filters/utils/generateSentimentReviewPayload';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  DataType,
  type TextDataSet,
} from './types';
import { isSentimentSnapshotPage } from './utils/sentiment-snapshot-page';

type SentimentReviewMetric = 'positive_review' | 'negative_review';

type SentimentReviewDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: SentimentReviewMetric;
  children: DataProviderChildren<TextDataSet | undefined>;
};

type ReviewType = 'pos' | 'neg';
type ReviewData = { success: true; review: string; reviewType: ReviewType };
type ReviewError = { success: false; error: string };

const getSelectionIdNumber = (selectionId: string): number | null => {
  const lastToken = selectionId.split('_').slice(-1)[0];
  const parsed = Number(lastToken);
  return Number.isFinite(parsed) ? parsed : null;
};

export const SentimentReviewDataProvider = ({
  primaryDimension,
  metric,
  children,
}: SentimentReviewDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters();
  const { config } = useTabContentConfig();
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);

  const isSnapshotDate = isSentimentSnapshotPage(config.id);

  const roleLeafIds = useRoleLeafIds(effectiveFilters);

  const reviewType: ReviewType = metric === 'negative_review' ? 'neg' : 'pos';

  const selectedPrimarySelection = (() => {
    const primarySelections = primaryFilter?.primarySelections ?? [];
    const active = primarySelections[0];
    return active ?? null;
  })();

  const payloadResult = (() => {
    if (!selectedPrimarySelection) return null;
    const id = getSelectionIdNumber(selectedPrimarySelection.id);
    if (id === null) return null;

    return generateSentimentPayload({
      filters: effectiveFilters,
      roleLeafIds,
      primaryFilter: primaryDimension,
      reviewType,
      selectedEntity: {
        id,
        label: selectedPrimarySelection.name,
        selectionListId: selectedPrimarySelection.levelId,
      },
      isSnapshotDate,
    });
  })();
  const payload =
    payloadResult instanceof MissingRoleLeafIdsError ? null : payloadResult;

  const query = useQuery({
    queryKey: ['sentiment-review', payload],
    queryFn: async (): Promise<ReviewData | ReviewError | null> => {
      if (!payload) return null;
      const requestID = crypto.randomUUID();

      const response = await dashMetaFetch('/api/sentiment/reviews', {
        method: 'POST',
        headers: { 'request-id': requestID },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new TanstackQueryError(
          'Failed to fetch sentiment reviews',
          requestID
        );
      }

      return response.json() as Promise<ReviewData | ReviewError>;
    },
    enabled: !!payload,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const status: DataProviderQueryStatus = (() => {
    if (payloadResult instanceof MissingRoleLeafIdsError) {
      return DataProviderQueryStatus.Error;
    }
    if (!payload) return DataProviderQueryStatus.Idle;
    if (query.isFetching) return DataProviderQueryStatus.Fetching;
    if (query.isError) return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  const data: TextDataSet | undefined = (() => {
    if (!selectedPrimarySelection) return undefined;
    if (!query.data) return undefined;
    const text =
      query.data.success === true ? query.data.review : query.data.error || '';

    return {
      type: DataType.TEXT,
      data: [
        {
          entity: selectedPrimarySelection.name,
          text,
          metadata:
            query.data.success === true
              ? { reviewType: query.data.reviewType }
              : undefined,
        },
      ],
    };
  })();

  const error = (() => {
    if (payloadResult instanceof MissingRoleLeafIdsError) return payloadResult;
    return query.error instanceof Error ? query.error : undefined;
  })();

  return children({ data, status, error });
};
